import { useState } from "react";
import { usePickupsInfiniteQuery } from "@features/pickups/hooks/usePickups";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { Card, CardContent } from "@shared/components/ui/card";
import { Package } from "lucide-react";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import PickupCard from "@/features/pickups/components/PickupCard";
import FilterPickups from "@/features/pickups/components/FilterPickups";
import HeadLayout from "@shared/layouts/HeadLayout";
import AddNewPickup from "./AddNewPickup";

const PickupList = () => {
  const [params, setParams] = useState({ limit: 10 });
  const auth = useAuth();

  const { t } = useTranslation();
  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePickupsInfiniteQuery(params);

  if (error) {
    toast.error("Error fetching pickups", error.message);
  }

  const pickups =
    data?.pages?.flatMap((page) => page.pickups || page.data) || [];

  const handleFilter = (newFilters) => {
    setParams((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
      page: 1,
    }));
  };

  const renderPickupContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <ThreeDotLoader size="md" color="primary" />
        </div>
      );
    }

    if (pickups.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t("No pickups found")}
            </h3>
            <p className="text-muted-foreground">
              {params.status
                ? t("No pickups match your current filters")
                : t("No pickups available yet")}
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-6">
          {pickups.map((pickup) => (
            <PickupCard key={pickup._id} pickup={pickup} viewMode="card"/>
          ))}
        </div>

        {hasNextPage && (
          <div className="flex justify-center pt-4">
            <InfiniteScrollTrigger
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <HeadLayout title={t("navigation.pickups")}>
        <AddNewPickup />
      </HeadLayout>

      {/* Filter Section */}
      <FilterPickups onFilter={handleFilter} currentFilters={params} />

      <div className="mt-6">{renderPickupContent()}</div>
    </>
  );
};

export default PickupList;
